import { Icon } from "../Icons";

export function VsJython({ showRow2, onContactClick }: { showRow2: boolean; onContactClick: () => void }) {
  return (
    <section className="section" id="vs-jython">
      <div className="section-inner">
        <div className="section-header">
          <div className="eyebrow">03 · Coprocessor vs Jython</div>
          <h2>Stop using tag events as a signal engine.</h2>
          <p className="lede">
            Use Jython for gateway glue. When tags become a stream, write the computation once as
            SQL and let a native C++ engine run the hot path on the gateway.
          </p>
        </div>

        <div className="card vs-card" style={{ padding: "42px 48px" }}>
          <div
            className="vs-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 28,
              alignItems: "stretch",
            }}
          >
            <div
              style={{
                border: "1px solid var(--line)",
                borderRadius: 6,
                padding: 24,
                background: "#FCFBF5",
              }}
            >
              <div
                className="mono"
                style={{
                  fontSize: 11,
                  color: "var(--ink-3)",
                  letterSpacing: "0.08em",
                  marginBottom: 10,
                }}
              >
                JYTHON VALUECHANGED
              </div>
              <div style={{ fontSize: 24, fontWeight: 500, letterSpacing: "-0.01em" }}>
                Callback lane
              </div>
              <div style={{ fontSize: 14, color: "var(--ink-3)", lineHeight: 1.55, marginTop: 10 }}>
                Good for glue. Wrong shape for continuous signal math across many tags.
              </div>
              <div style={{ display: "grid", gap: 10, marginTop: 22, fontSize: 13 }}>
                {[
                  "Default 3-thread tag-event pool",
                  "Callback code spreads across tags and UDTs",
                  "Queue pressure can surface as missedEvents",
                  "No single signal-processing graph",
                ].map((t, i) => (
                  <div key={i} style={{ display: "flex", gap: 10 }}>
                    <Icon.Warn style={{ color: "var(--warn)", marginTop: 2, flex: "0 0 14px" }} />
                    <span style={{ color: "var(--ink-2)" }}>{t}</span>
                  </div>
                ))}
              </div>
            </div>

            <div
              style={{
                border: "1px solid var(--accent)",
                borderRadius: 6,
                padding: 24,
                background: "#FFFDF2",
                boxShadow: "0 1px 0 rgba(0,0,0,0.04) inset",
              }}
            >
              <div
                className="mono"
                style={{
                  fontSize: 11,
                  color: "var(--ink-3)",
                  letterSpacing: "0.08em",
                  marginBottom: 10,
                }}
              >
                COPROCESSOR
              </div>
              <div style={{ fontSize: 24, fontWeight: 500, letterSpacing: "-0.01em" }}>
                Signal engine lane
              </div>
              <div style={{ fontSize: 14, color: "var(--ink-3)", lineHeight: 1.55, marginTop: 10 }}>
                One SQL pipeline, native workers, live tags in, derived signals out.
              </div>
              <div style={{ display: "grid", gap: 10, marginTop: 22, fontSize: 13 }}>
                {[
                  "One SQL graph instead of scattered callbacks",
                  "Scales with available cores",
                  "Per-processor metrics and operations view",
                  "No outbound telemetry or cloud processing",
                ].map((t, i) => (
                  <div key={i} style={{ display: "flex", gap: 10 }}>
                    <Icon.Check style={{ color: "var(--ok)", marginTop: 2, flex: "0 0 14px" }} />
                    <span style={{ color: "var(--ink-2)" }}>{t}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {showRow2 && (
            <div style={{ borderTop: "1px solid var(--line)", paddingTop: 26, marginTop: 28 }}>
              <div
                className="vs-split"
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) auto",
                  gap: 24,
                  alignItems: "end",
                  marginBottom: 18,
                }}
              >
                <div>
                  <div
                    className="mono"
                    style={{
                      fontSize: 11,
                      color: "var(--ink-3)",
                      letterSpacing: "0.08em",
                      marginBottom: 8,
                    }}
                  >
                    WHERE IT GETS INTERESTING
                  </div>
                  <div
                    style={{
                      fontSize: 22,
                      lineHeight: 1.18,
                      fontWeight: 500,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    Same computation. 10x more room.
                  </div>
                </div>
                <div
                  className="mono"
                  style={{ fontSize: 12, color: "var(--ink-3)", textAlign: "right" }}
                >
                  same gateway
                  <br />
                  same payload
                  <br />
                  SQL-equivalent workload
                </div>
              </div>

              <div
                className="capacity-bars"
                style={{
                  border: "1px solid var(--line)",
                  borderRadius: 6,
                  background: "#fff",
                  padding: 22,
                }}
              >
                <div style={{ display: "grid", gap: 18 }}>
                  <div
                    className="capacity-bar-row"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "230px minmax(0, 1fr) 56px",
                      gap: 16,
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <div
                        className="mono"
                        style={{
                          fontSize: 10.5,
                          color: "var(--ink-3)",
                          letterSpacing: "0.08em",
                        }}
                      >
                        BASELINE
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>
                        Default tag-event path
                      </div>
                    </div>
                    <div
                      style={{
                        height: 28,
                        border: "1px solid var(--line)",
                        borderRadius: 4,
                        background: "#F3F2EC",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        className="motion-bar"
                        style={{
                          width: "10%",
                          height: "100%",
                          background: "var(--ink-4)",
                          transformOrigin: "left center",
                        }}
                      />
                    </div>
                    <div
                      className="mono tnum"
                      style={{ fontSize: 24, lineHeight: 1, textAlign: "right" }}
                    >
                      1x
                    </div>
                  </div>

                  <div
                    className="capacity-bar-row"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "230px minmax(0, 1fr) 56px",
                      gap: 16,
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <div
                        className="mono"
                        style={{
                          fontSize: 10.5,
                          color: "var(--ink-3)",
                          letterSpacing: "0.08em",
                        }}
                      >
                        COPROCESSOR · 8-CORE M4 RUN
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>
                        Native workers on the hot path
                      </div>
                    </div>
                    <div
                      style={{
                        height: 28,
                        border: "1px solid #B8900F",
                        borderRadius: 4,
                        background: "#FBF6DA",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        className="motion-bar"
                        style={{
                          width: "100%",
                          height: "100%",
                          background: "var(--accent)",
                          transformOrigin: "left center",
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          left: "14%",
                          top: 0,
                          bottom: 0,
                          width: 1,
                          background: "rgba(26,24,20,.35)",
                        }}
                      />
                      <div
                        className="mono"
                        style={{
                          position: "absolute",
                          left: "calc(14% + 6px)",
                          top: 6,
                          fontSize: 10,
                          color: "var(--ink-2)",
                        }}
                      >
                        ~1.5x one worker
                      </div>
                    </div>
                    <div
                      className="mono tnum"
                      style={{
                        fontSize: 24,
                        lineHeight: 1,
                        textAlign: "right",
                        color: "var(--ink)",
                      }}
                    >
                      10x
                    </div>
                  </div>
                </div>

                <div
                  className="vs-split"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: 18,
                    alignItems: "center",
                    marginTop: 16,
                    paddingTop: 14,
                    borderTop: "1px solid var(--line)",
                  }}
                >
                  <div style={{ fontSize: 12.5, color: "var(--ink-3)", lineHeight: 1.45 }}>
                    10x means the same equivalent computation at 10x throughput, or roughly 10x the
                    tags handled without missed events.
                  </div>
                  <div
                    className="mono"
                    style={{ fontSize: 11, color: "var(--ink-3)", whiteSpace: "nowrap" }}
                  >
                    8-core M4 run
                  </div>
                </div>
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 28, flexWrap: "wrap" }}>
            <span className="chip chip-ok">
              <Icon.Check /> Native engine inside Ignition
            </span>
            <span className="chip">Default Jython tag-event pool: 3 threads</span>
            <span className="chip">No cloud round-trip</span>
          </div>

          <div
            style={{
              fontSize: 12,
              color: "var(--ink-3)",
              marginTop: 20,
              lineHeight: 1.55,
              maxWidth: 900,
            }}
          >
            <span
              className="mono"
              style={{ fontSize: 11, color: "var(--ink-4)", letterSpacing: "0.06em" }}
            >
              METHODOLOGY ·{" "}
            </span>
            Same gateway, same payload, SQL-equivalent workload. Jython measured through Ignition
            tag-event scripts on the default 3-thread pool; Coprocessor measured through the native
            module runtime on a Mac M4 with 8 cores allocated. Results vary by payload, SQL,
            cadence, JVM settings, workers, and CPU allocation.{" "}
          </div>

          <div
            className="vs-split"
            style={{
              marginTop: 24,
              paddingTop: 20,
              borderTop: "1px solid var(--line)",
              display: "grid",
              gridTemplateColumns: "1fr auto",
              gap: 18,
              alignItems: "center",
            }}
          >
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: "-0.01em" }}>
                Got a tag script doing too much?
              </div>
              <div
                style={{ fontSize: 13, color: "var(--ink-3)", lineHeight: 1.5, marginTop: 4 }}
              >
                Send tag count, cadence, payload shape, and what the script computes. We will
                sanity-check the fit.
              </div>
            </div>
            <button onClick={onContactClick} className="btn btn-secondary">
              Sanity-check a workload
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
